import { REGION_ENUM } from '../common/constants/region';

const SLCompass = Mach.requireModule('SLCompass');
const SLCommon = Mach.requireModule('SLCommon');

interface IDomainKeyInfo {
  Test: string;
  Prod: string;
  Stage?: string;
}

interface IDomainHostInfo {
  Test: string;
  Prod: string;
  Stage?: string;
}

/**
 * 通过桥获取域名
 * @param key
 * @returns
 */
function getDomainWithKey(key: string) {
  return SLCompass?.getDomainByCompassKey?.(key);
}

/**
 * 拼接泳道
 * @param domain
 * @param swimlane
 * @returns
 */
function appendSwimline(domain: string, swimlaneName: string) {
  // 泳道名为空返回原始域名
  if (!swimlaneName || typeof swimlaneName !== 'string') {
    return domain;
  }

  const [protocol, domainWithoutProtocol] = domain.split('://');
  if (!protocol || !domainWithoutProtocol) {
    return domain; // 返回原始域名
  }
  // 在域名中拼接swimlane参数'https://${swimlane.name}-sl-ocean.waimai.test.sankuai.com'
  return `${protocol}://${swimlaneName}-sl-${domainWithoutProtocol}`;
}

/**
 * 域名解析处理
 * @param domainKeyInfo
 * @param domainHostInfo
 * @returns
 */
function resolveDomainHost(domainKeyInfo: IDomainKeyInfo, domainHostInfo: IDomainHostInfo) {
  if (!domainKeyInfo || !domainHostInfo) {
    return '';
  }
  const { env = 'Prod', swimlane } = SLCommon?.getEnv?.() || {};
  // 仅测试环境检查拼接泳道名称
  const swimlaneName = swimlane?.name || '';
  if (Mach.env.isDebug && swimlaneName) {
    const domainKey = domainKeyInfo?.Test;
    let domain = getDomainWithKey(domainKey) || domainHostInfo?.Test;
    // 获取域名飞字符串或为空这使用默认域名
    if (!domain || typeof domain !== 'string') {
      return '';
    }
    if (domain && !domain?.startsWith?.('http')) {
      domain = `https://${domain}`;
    }
    return appendSwimline(domain, swimlaneName);
  } else {
    const domainKey = domainKeyInfo?.[env] || domainKeyInfo?.Prod;
    let domain = getDomainWithKey(domainKey) || domainHostInfo?.[env] || domainHostInfo?.Prod || '';
    if (domain && !domain?.startsWith?.('http')) {
      domain = `https://${domain}`;
    }
    return domain;
  }
}

/**
 * 获取i18n域名
 * @returns {string} i18n域名
 */
export function getI18nDomainHost() {
  const domainKeyInfo: IDomainKeyInfo = {
    Test: 'i18n.i18n.testHost',
    Prod: 'i18n.i18n.host',
  };
  const domainHostInfo: IDomainHostInfo = {
    Test: 'https://ocean.waimai.test.sankuai.com',
    Prod: 'https://i18n.mykeeta.com',
  };
  return resolveDomainHost(domainKeyInfo, domainHostInfo);
}

/**
 * 获取账号域名
 * @returns
 */
export function getPassportDomainHost() {
  const domainKeyMapping = {
    Test: 'Passport.testUrl',
    Prod: 'Passport.url',
  };
  const domainHostMapping = {
    Test: 'https://passport-h.wpt.test.sankuai.com',
    Prod: 'https://passport-hk.mykeeta.com',
  };
  return resolveDomainHost(domainKeyMapping, domainHostMapping);
}

/**
 * 客服域名
 * @returns
 */
export function getCSDomainHost() {
  const domainKeyMapping = {
    Test: 'Keeta.CustomerServices.TestUrl',
    Stage: 'Keeta.CustomerServices.StUrl',
    Prod: 'Keeta.CustomerServices.ProdUrl',
  };
  const domainHostMapping = {
    Test: 'https://cs.mykeeta.test.sankuai.com',
    Stage: 'https://cs.mykeeta.st.sankuai.com',
    Prod: 'https://cs.mykeeta.com',
  };
  return resolveDomainHost(domainKeyMapping, domainHostMapping);
}

/**
 * 获取实验域名
 * @returns
 */
export function getExpDomainHost(env: string = 'prod') {
  const domainKeyMapping = {
    test: 'Keeta.C.TestUrl',
    prod: 'Keeta.C.ProductUrl',
    stage: 'Keeta.C.StUrl',
  };
  const defaultDomainMapping = {
    test: 'http://fooddelivery.mykeeta.test.sankuai.com',
    stage: 'https://fooddelivery.mykeeta.st.sankuai.com',
    prod: 'https://fooddelivery.mykeeta.sankuai.com',
  };
  const domainKey = domainKeyMapping[env];
  return getDomainWithKey(domainKey) || defaultDomainMapping[env];
}

/**
 * 路径规划域名
 * @returns
 */
export function getLBSRoutePlanDomainHost() {
  const domainKeyMapping = {
    Test: 'Keeta.RoutePlan.TestUrl',
    Prod: 'Keeta.RoutePlan.ProdUrl',
  };
  const domainHostMapping = {
    Test: 'http://lbsapi.map.test.sankuai.com',
    Prod: 'https://lbsapi.mykeeta.com',
  };
  return resolveDomainHost(domainKeyMapping, domainHostMapping);
}

/**
 * 获取当前 region（统一转大写，避免原生侧大小写不稳定导致的误判）
 * @returns
 */
function getCurrentRegion(): string {
  try {
    const { region } = SLCompass?.getCompassInfo?.() || {};
    return region ? region.toUpperCase() : '';
  } catch (e) {
    return '';
  }
}

/**
 * 获取无人机 KNB 容器 uid（区分环境和 region）
 * 文档: https://km.sankuai.com/collabpage/2754127889
 * @returns knb_uid 字符串，若为 st/prod 环境且尚未部署则返回空字符串
 */
export function getDroneKnbUid(): string {
  const region = getCurrentRegion();
  const { env = 'Prod' } = SLCommon?.getEnv?.() || {};
  // HK 和 SA 都走香港机房
  const isHKRegion = [REGION_ENUM.HongKong, REGION_ENUM.SaudiArabia].includes(region);

  const knbUidMap: Record<string, Record<string, string>> = {
    Test: { HK: 't41041011004eb95345ed01be6f71241', AE: 't41041011001526856f2db435a474a31' },
    Stage: { HK: 't41041011006be1003c9ee791624a871', AE: 't41041011003218fee22ff1d76ecd081' },
    Prod: { HK: 'k4004101140967d4105515b1ae775f71', AE: 'k4004101140967d4105515b1ae775f71' },
  };

  const regionKey = isHKRegion ? 'HK' : 'AE';
  const envMap = knbUidMap[env] || knbUidMap.Prod;
  return envMap[regionKey] || '';
}

/**
 * 无人机域名
 * 区分香港机房(HK/SA)和其他机房(AE等)，使用不同的兜底域名
 * 泳道环境下直接使用测试环境域名（无人机服务不支持泳道）
 * @returns
 */
export function getDroneDomainHost() {
  const region = getCurrentRegion();
  // HK 和 SA 都走香港机房
  const isHKRegion = [REGION_ENUM.HongKong, REGION_ENUM.SaudiArabia].includes(region);

  const domainKeyMapping = {
    Test: 'Drone.C.TestUrl',
    Stage: 'Drone.C.StUrl',
    Prod: 'Drone.C.ProdUrl',
  };

  // 区分香港机房和其他机房的兜底域名
  const domainHostMappingHK = {
    Test: 'https://drone.mykeeta.test.sankuai.com',
    Stage: 'https://drone.mykeeta.st.sankuai.com',
    Prod: 'https://drone-hk.mykeeta.com',
  };
  const domainHostMappingEU = {
    Test: 'https://drone-eu.sailor.test.keetapp.com',
    Stage: 'https://drone-eu.sailor.st.keetapp.com',
    Prod: 'https://drone-eu.mykeeta.com',
  };

  const domainHostMapping = isHKRegion ? domainHostMappingHK : domainHostMappingEU;

  // 泳道环境下使用测试环境域名（跳过泳道拼接）
  const { swimlane } = SLCommon?.getEnv?.() || {};
  const swimlaneName = swimlane?.name || '';
  if (Mach.env.isDebug && swimlaneName) {
    const domainKey = domainKeyMapping.Test;
    let domain = getDomainWithKey(domainKey) || domainHostMapping.Test;
    if (domain && !domain.startsWith('http')) {
      domain = `https://${domain}`;
    }
    return domain;
  }

  return resolveDomainHost(domainKeyMapping, domainHostMapping);
}
